// src/components/dashboard/AgentsTable.jsx
import { useContext, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { format } from "date-fns";
import { LanguageContext } from "../context/LanguageContext";

function formatTs(ts) {
  try {
    const d =
      typeof ts?.toDate === "function"
        ? ts.toDate()
        : ts instanceof Date
        ? ts
        : null;
    return d ? format(d, "yyyy-MM-dd HH:mm") : "—";
  } catch {
    return "—";
  }
}

export default function AgentsTable({
  rows = [],
  emptyLabel = "No data",
  dense = false,
  tokenByAgent = {},
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { translate } = useContext(LanguageContext);
  const numberFormatter = useMemo(() => new Intl.NumberFormat(), []);
  if (!rows.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">{emptyLabel}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <Table size={dense ? "small" : "medium"}>
        <TableHead>
          <TableRow sx={{ "& th": { fontWeight: 600 } }}>
            <TableCell>Name</TableCell>
            {!isMobile && <TableCell>Created</TableCell>}
            {!isMobile && (
              <TableCell align="right">
                {translate?.("Tokens") || "Tokens"}
              </TableCell>
            )}
            <TableCell>{translate?.("Status") || "Status"}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((a) => {
            const status = a.active === false ? "Inactive" : "Active";
            const tokenValue =
              typeof tokenByAgent[a.id] === "number"
                ? numberFormatter.format(Math.round(tokenByAgent[a.id]))
                : "—";
            return (
              <TableRow key={a.id} hover>
                <TableCell>
                  <Typography fontWeight={600}>{a.name || "—"}</Typography>
                  {isMobile && (
                    <Typography variant="caption" color="text.secondary">
                      {formatTs(a.createdAt)}
                    </Typography>
                  )}
                </TableCell>
                {!isMobile && <TableCell>{formatTs(a.createdAt)}</TableCell>}
                {!isMobile && (
                  <TableCell align="right">{tokenValue}</TableCell>
                )}
                <TableCell>
                  <Box
                    component="span"
                    sx={{
                      px: 1.2,
                      py: 0.4,
                      borderRadius: 99,
                      fontSize: 12,
                      bgcolor:
                        status === "Active"
                          ? theme.palette.mode === "dark"
                            ? "#244c34"
                            : "#dcfce7"
                          : theme.palette.mode === "dark"
                          ? "#4b5563"
                          : "#e5e7eb",
                      color:
                        status === "Active"
                          ? theme.palette.mode === "dark"
                            ? "#a6da95"
                            : "#166534"
                          : theme.palette.mode === "dark"
                          ? "#e5e7eb"
                          : "#4b5563",
                    }}
                  >
                    {status}
                  </Box>
                  {isMobile && (
                    <Typography variant="caption" color="text.secondary">
                      {translate?.("Tokens") || "Tokens"}: {tokenValue}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
